import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import SoftwareBoxMockup from './SoftwareBoxMockup';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const FALLBACK_PRODUCTS = [
    {
        id: 1, category_id: 1,
        name_ka: 'ელექტრონული მუნიციპალიტეტი & სერვისები',
        name_en: 'Municipal Citizen Portal & Services',
        slug: 'municipal-citizen-portal',
        tagline_ka: 'მოქალაქეთა ონლაინ განცხადებები და მუნიციპალური სერვისები',
        tagline_en: 'Citizen applications, digital queue and administrative services',
        icon: '🏛️', badge_color: '#2563eb', target_market: 'municipal',
        setup_price: '18000.00', annual_price: '6000.00',
        features_ka: ['ონლაინ განცხადებების პორტალი', 'ელექტრონული რიგის მართვა', 'SMS/Email შეტყობინებები', 'მერიის ადმინ პანელი'],
        features_en: ['Online applications portal', 'Digital queue management', 'SMS/Email notifications', 'City Hall admin panel'],
        is_active: 1, is_featured: 1,
        category_name_ka: 'ციფრული მმართველობა', category_name_en: 'Digital Governance'
    },
    {
        id: 2, category_id: 1,
        name_ka: 'საქმისწარმოების & დოკუმენტბრუნვის სისტემა',
        name_en: 'Electronic Document & Case Management',
        slug: 'electronic-document-flow',
        tagline_ka: 'შიდა მუნიციპალური დოკუმენტბრუნვა და ციფრული ხელმოწერა',
        tagline_en: 'Internal document workflow, digital signature and archival',
        icon: '📑', badge_color: '#7c3aed', target_market: 'government',
        setup_price: '15000.00', annual_price: '5000.00',
        features_ka: ['ციფრული ხელმოწერის მხარდაჭერა', 'ავტომატური ნუმერაცია', 'დავალებების მონიტორინგი', 'როლებზე დაფუძნებული წვდომა'],
        features_en: ['Digital signature integration', 'Automated numbering', 'Task monitoring', 'Role-based access'],
        is_active: 1, is_featured: 0,
        category_name_ka: 'ციფრული მმართველობა', category_name_en: 'Digital Governance'
    },
    {
        id: 3, category_id: 1,
        name_ka: 'მოქალაქეთა ჩართულობა & პეტიციები',
        name_en: 'Citizen Engagement & Petition Platform',
        slug: 'citizen-engagement',
        tagline_ka: 'რუკაზე დაფუძნებული პრობლემების დაფიქსირება და კენჭისყრა',
        tagline_en: 'Map-based issue reporting, online petitions and public voting',
        icon: '🗣️', badge_color: '#059669', target_market: 'municipal',
        setup_price: '12000.00', annual_price: '4000.00',
        features_ka: ['რუკაზე პრობლემის მონიშვნა', 'ონლაინ პეტიციები', 'საჯარო გამოკითხვები', 'ფოტო-ფიქსაცია'],
        features_en: ['Map-based issue reporting', 'Photo attachment', 'Online petitions', 'Public voting'],
        is_active: 1, is_featured: 0,
        category_name_ka: 'ციფრული მმართველობა', category_name_en: 'Digital Governance'
    },
    {
        id: 4, category_id: 1,
        name_ka: 'GIS მუნიციპალური რუკა & ზონირება',
        name_en: 'Municipal GIS Mapping & Zoning',
        slug: 'municipal-gis',
        tagline_ka: 'ინტერაქტიული GIS შრეები, ინფრასტრუქტურა, ზონირება',
        tagline_en: 'Interactive GIS layers, infrastructure, zoning visualization',
        icon: '🗺️', badge_color: '#0891b2', target_market: 'municipal',
        setup_price: '22000.00', annual_price: '7500.00',
        features_ka: ['ინტერაქტიული GIS შრეები', 'ზონირების ჩვენება', 'ინფრასტრუქტურის ობიექტები', 'ნებართვების კონტროლი'],
        features_en: ['Interactive GIS layers', 'Zoning visualization', 'Infrastructure objects', 'Permit management'],
        is_active: 1, is_featured: 0,
        category_name_ka: 'ციფრული მმართველობა', category_name_en: 'Digital Governance'
    },
    {
        id: 8, category_id: 2,
        name_ka: 'მუნიციპალური მობილური აპლიკაცია (iOS / Android)',
        name_en: 'Citizen Mobile App (iOS / Android)',
        slug: 'citizen-mobile-app',
        tagline_ka: 'ყველა მუნიციპალური სერვისი ერთ აპლიკაციაში',
        tagline_en: 'All municipal services in one mobile app',
        icon: '📱', badge_color: '#9333ea', target_market: 'municipal',
        setup_price: '28000.00', annual_price: '9000.00',
        features_ka: ['ყველა სერვისი ერთ სივრცეში', 'Push შეტყობინებები', 'ციფრული საშვები', 'ონლაინ გადახდები'],
        features_en: ['All services in one place', 'Push notifications', 'Digital passes', 'Online payments'],
        is_active: 1, is_featured: 1,
        category_name_ka: 'მობილური აპლიკაციები', category_name_en: 'Mobile Applications'
    },
    {
        id: 6, category_id: 1,
        name_ka: 'საგანგებო შეტყობინებები & SMS/Push მაუწყებელი',
        name_en: 'Emergency Broadcast & SMS Alerts',
        slug: 'emergency-alerts',
        tagline_ka: 'მყისიერი კრიზისული SMS, Push, WEB შეტყობინებები',
        tagline_en: 'Instant crisis SMS, Push, and WEB emergency broadcasts',
        icon: '🚨', badge_color: '#dc2626', target_market: 'municipal',
        setup_price: '9500.00', annual_price: '3500.00',
        features_ka: ['SMS მასობრივი გაგზავნა', 'Push შეტყობინებები', 'კატეგორიზებული განგაში', 'სამიზნე რაიონები'],
        features_en: ['Mass SMS broadcasting', 'Push alerts', 'Categorized alert tiers', 'Geo-targeted districts'],
        is_active: 1, is_featured: 0,
        category_name_ka: 'ციფრული მმართველობა', category_name_en: 'Digital Governance'
    },
];

const HomeProducts = () => {
    const { i18n } = useTranslation();
    const { lang: urlLang } = useParams();
    const navigate = useNavigate();
    const currentLang = urlLang || i18n.language || 'en';

    const [products, setProducts] = useState(FALLBACK_PRODUCTS);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [isPaused, setIsPaused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const trackRef = useRef(null);
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);

    useEffect(() => {
        let isMounted = true;
        const fetchCatalog = async () => {
            setLoading(true);
            try {
                const [pRes, cRes] = await Promise.all([
                    fetch(`${API_URL}products.php`).catch(() => null),
                    fetch(`${API_URL}products.php?type=categories`).catch(() => null)
                ]);
                if (pRes && pRes.ok) {
                    const pData = await pRes.json();
                    if (isMounted && Array.isArray(pData) && pData.length > 0) {
                        const activeProducts = pData.filter(p => p.is_active === 1 || p.is_active === '1');
                        setProducts(activeProducts.length > 0 ? activeProducts : pData);
                    }
                }
                if (cRes && cRes.ok) {
                    const cData = await cRes.json();
                    if (isMounted && Array.isArray(cData)) setCategories(cData);
                }
            } catch (err) {
                console.error('Error fetching products for homepage slider:', err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchCatalog();
        return () => { isMounted = false; };
    }, []);

    const filteredProducts = selectedCategory === 'all'
        ? products
        : products.filter(p => String(p.category_id) === String(selectedCategory));

    useEffect(() => {
        if (trackRef.current) {
            trackRef.current.scrollTo({ left: 0, behavior: 'smooth' });
            setActiveIndex(0);
        }
    }, [selectedCategory]);

    const scroll = useCallback((direction) => {
        const track = trackRef.current;
        if (!track) return;
        const slide = track.querySelector('.hp-slide-item');
        const slideWidth = slide ? slide.offsetWidth + 32 : 400;
        track.scrollBy({ left: slideWidth * direction, behavior: 'smooth' });
    }, []);

    const scrollToIndex = (idx) => {
        const track = trackRef.current;
        if (!track) return;
        const slides = track.querySelectorAll('.hp-slide-item');
        if (slides[idx]) {
            slides[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            setActiveIndex(idx);
        }
    };

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        const handleScroll = () => {
            const slide = track.querySelector('.hp-slide-item');
            if (!slide) return;
            const slideWidth = slide.offsetWidth + 32;
            const currentIdx = Math.round(track.scrollLeft / slideWidth);
            setActiveIndex(Math.min(currentIdx, Math.max(0, filteredProducts.length - 1)));
        };
        track.addEventListener('scroll', handleScroll, { passive: true });
        return () => track.removeEventListener('scroll', handleScroll);
    }, [filteredProducts.length]);

    useEffect(() => {
        if (loading || filteredProducts.length <= 1 || isPaused) return;
        const interval = setInterval(() => {
            const track = trackRef.current;
            if (!track) return;
            const maxScroll = track.scrollWidth - track.clientWidth;
            const slide = track.querySelector('.hp-slide-item');
            const step = slide ? slide.offsetWidth + 32 : 400;
            if (track.scrollLeft >= maxScroll - 20) {
                track.scrollTo({ left: 0, behavior: 'smooth' });
                setActiveIndex(0);
            } else {
                track.scrollBy({ left: step, behavior: 'smooth' });
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [loading, filteredProducts.length, isPaused]);

    const handleMouseDown = (e) => {
        const track = trackRef.current;
        if (!track) return;
        isDraggingRef.current = true;
        startXRef.current = e.pageX - track.offsetLeft;
        scrollLeftRef.current = track.scrollLeft;
        track.style.cursor = 'grabbing';
        setIsPaused(true);
    };

    const handleMouseMove = (e) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();
        const track = trackRef.current;
        if (!track) return;
        const x = e.pageX - track.offsetLeft;
        const walk = (x - startXRef.current) * 1.4;
        track.scrollLeft = scrollLeftRef.current - walk;
    };

    const handleMouseUpOrLeave = () => {
        isDraggingRef.current = false;
        if (trackRef.current) trackRef.current.style.cursor = 'grab';
    };

    return (
        <section
            className="hp-section"
            id="products"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => { handleMouseUpOrLeave(); setIsPaused(false); }}
        >
            <div className="container">

                {/* ── Section Header ── */}
                <motion.div
                    className="hp-header"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.55 }}
                >
                    <div className="hp-eyebrow">
                        <span className="hp-eyebrow-line" />
                        <span className="hp-eyebrow-text">
                            {currentLang === 'ka' ? 'პროგრამული პაკეტები' : 'Software Product Catalog'}
                        </span>
                        <span className="hp-eyebrow-line" />
                    </div>
                    <h2 className="hp-title">
                        {currentLang === 'ka'
                            ? 'მზა ციფრული გადაწყვეტილებები & პლატფორმები'
                            : 'Turnkey Enterprise Software Suites'}
                    </h2>
                    <p className="hp-subtitle">
                        {currentLang === 'ka'
                            ? 'სტანდარტიზებული, უსაფრთხო და დოკუმენტირებული მუნიციპალური პლატფორმები — სრული ლიცენზიით, გარანტიით და ტექნიკური მხარდაჭერით.'
                            : 'Standardized, compliant and battle-tested municipal & enterprise software — complete with full documentation, ISO certification and deployment support.'}
                    </p>
                    <div className="hp-header-actions mt-3">
                        <Link 
                            to={`/${currentLang}/products`} 
                            className="hp-header-catalog-link"
                            title={currentLang === 'ka' ? 'სრული პროდუქტების კატალოგი' : 'Explore Full Products Catalog'}
                        >
                            <i className="fas fa-th-large me-2" />
                            <span>{currentLang === 'ka' ? 'სრული პროდუქტების კატალოგი' : 'Explore Full Products Catalog'}</span>
                            <i className="fas fa-arrow-right ms-2" />
                        </Link>
                    </div>
                </motion.div>

                {/* ── Category Tabs ── */}
                {categories.length > 0 && (
                    <div className="hp-cat-tabs">
                        <button
                            className={`hp-cat-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                            onClick={() => setSelectedCategory('all')}
                        >
                            {currentLang === 'ka' ? 'ყველა' : 'All Solutions'}
                            <span className="hp-cat-count">{products.length}</span>
                        </button>
                        {categories.map(cat => {
                            const count = products.filter(p => String(p.category_id) === String(cat.id)).length;
                            return (
                                <button
                                    key={cat.id}
                                    className={`hp-cat-btn ${selectedCategory === String(cat.id) ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(String(cat.id))}
                                >
                                    {currentLang === 'ka' ? cat.name_ka : (cat.name_en || cat.name_ka)}
                                    {count > 0 && <span className="hp-cat-count">{count}</span>}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* ── Controls Bar ── */}
                <div className="hp-controls-bar">
                    <div className="hp-controls-left">
                        <span className="hp-slide-counter">
                            <span className="hp-counter-n">{String(activeIndex + 1).padStart(2, '0')}</span>
                            <span className="hp-counter-sep"> / </span>
                            <span className="hp-counter-t">{String(filteredProducts.length).padStart(2, '0')}</span>
                        </span>
                        <span className="hp-controls-hint">
                            {currentLang === 'ka' ? 'გადაიტანეთ ან გამოიყენეთ ისრები' : 'Drag or use arrows to browse'}
                        </span>
                    </div>
                    <div className="hp-controls-right">
                        <button
                            className="hp-ctrl-btn"
                            onClick={() => setIsPaused(!isPaused)}
                            title={isPaused ? 'Resume' : 'Pause'}
                        >
                            <i className={`fas ${isPaused ? 'fa-play' : 'fa-pause'}`} />
                        </button>
                        <button 
                            className="hp-arrow-btn" 
                            onClick={() => scroll(-1)} 
                            aria-label="Previous"
                            title={currentLang === 'ka' ? 'წინა პროდუქტები' : 'Previous products'}
                        >
                            <i className="fas fa-chevron-left" />
                        </button>
                        <button 
                            className="hp-arrow-btn" 
                            onClick={() => scroll(1)} 
                            aria-label="Next"
                            title={currentLang === 'ka' ? 'შემდეგი პროდუქტები' : 'Next products'}
                        >
                            <i className="fas fa-chevron-right" />
                        </button>
                    </div>
                </div>

                {/* ── Slider Track ── */}
                <div
                    className="hp-track"
                    ref={trackRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUpOrLeave}
                    onMouseLeave={handleMouseUpOrLeave}
                >
                    {filteredProducts.map((product, index) => {
                        const productName = currentLang === 'ka'
                            ? (product.name_ka || product.name_en)
                            : (product.name_en || product.name_ka);
                        const productTagline = currentLang === 'ka'
                            ? (product.tagline_ka || product.tagline_en)
                            : (product.tagline_en || product.tagline_ka);
                        const features = (currentLang === 'ka'
                            ? product.features_ka
                            : (product.features_en && product.features_en.length > 0
                                ? product.features_en
                                : product.features_ka)) || [];
                        const detailUrl = `/${currentLang}/products/${product.slug || product.id}`;
                        const accent = product.badge_color || '#6c5ce7';
                        const categoryName = currentLang === 'ka'
                            ? (product.category_name_ka || product.category_name_en)
                            : (product.category_name_en || product.category_name_ka);

                        return (
                            <div className="hp-slide-item" key={product.id || index}>
                                <div
                                    className="hp-card"
                                    style={{ '--accent': accent }}
                                    onClick={() => navigate(detailUrl)}
                                >
                                    {/* Accent top bar */}
                                    <div className="hp-card-topbar" style={{ background: accent }} />

                                    {/* Box showcase — centered, prominent */}
                                    <div className="hp-box-stage">
                                        <SoftwareBoxMockup
                                            product={product}
                                            lang={currentLang}
                                            onClick={() => navigate(detailUrl)}
                                        />
                                    </div>

                                    {/* Divider */}
                                    <div className="hp-card-divider" />

                                    {/* Product info below the box */}
                                    <div className="hp-card-body">
                                        {/* Category + Featured row */}
                                        <div className="hp-card-row mb-2">
                                            <span className="hp-tag-cat">{categoryName || 'Platform'}</span>
                                            {product.is_featured === 1 && (
                                                <span className="hp-tag-featured">Featured</span>
                                            )}
                                        </div>

                                        {/* Title */}
                                        <h3 className="hp-card-title">{productName}</h3>

                                        {/* Tagline */}
                                        {productTagline && (
                                            <p className="hp-card-tagline">{productTagline}</p>
                                        )}

                                        {/* Feature checklist */}
                                        {features.length > 0 && (
                                            <ul className="hp-feat-list">
                                                {features.slice(0, 3).map((f, fi) => (
                                                    <li key={fi} className="hp-feat-item">
                                                        <span className="hp-feat-dot" style={{ background: accent }} />
                                                        {f}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        {/* Price row */}
                                        <div className="hp-price-row">
                                            <div>
                                                <span className="hp-price-lbl">
                                                    {currentLang === 'ka' ? 'დანერგვა' : 'Setup'}
                                                </span>
                                                <span className="hp-price-val">
                                                    {parseFloat(product.setup_price) > 0
                                                        ? `₾${parseFloat(product.setup_price).toLocaleString()}`
                                                        : (currentLang === 'ka' ? 'მოთხოვნით' : 'On Request')}
                                                </span>
                                            </div>
                                            {parseFloat(product.annual_price) > 0 && (
                                                <div className="text-end">
                                                    <span className="hp-price-lbl">ARR</span>
                                                    <span className="hp-price-arr">
                                                        ₾{parseFloat(product.annual_price).toLocaleString()}<small>/yr</small>
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* CTA */}
                                        <Link
                                            to={detailUrl}
                                            className="hp-cta-btn"
                                            style={{ '--accent': accent }}
                                            onClick={e => e.stopPropagation()}
                                            title={productName}
                                        >
                                            <span>{currentLang === 'ka' ? 'სრულად ნახვა' : 'View Product'}</span>
                                            <i className="fas fa-arrow-right" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── Dot Pagination ── */}
                {filteredProducts.length > 1 && (
                    <div className="hp-dots">
                        {filteredProducts.map((_, i) => (
                            <button
                                key={i}
                                className={`hp-dot ${activeIndex === i ? 'active' : ''}`}
                                onClick={() => scrollToIndex(i)}
                                aria-label={`Slide ${i + 1}`}
                            />
                        ))}
                    </div>
                )}

                {/* ── Footer CTA ── */}
                <div className="hp-footer-cta">
                    <Link to={`/${currentLang}/products`} className="hp-footer-link">
                        <i className="fas fa-th-large me-2" />
                        {currentLang === 'ka' ? 'სრული პროდუქტების კატალოგი' : 'Browse Full Product Catalog'}
                        <i className="fas fa-arrow-right ms-2" />
                    </Link>
                    <Link to={`/${currentLang}/contact`} className="hp-footer-link-sec">
                        <i className="fas fa-envelope me-2" />
                        {currentLang === 'ka' ? 'ინდივიდუალური შეთავაზება' : 'Request Custom Quote'}
                    </Link>
                </div>

            </div>

            <style>{`
                /* ── Section wrapper ── */
                .hp-section {
                    padding: 110px 0 120px;
                    position: relative;
                    overflow: hidden;
                }
                .hp-section::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background:
                        radial-gradient(ellipse 70% 50% at 20% 50%, rgba(108,92,231,0.07) 0%, transparent 70%),
                        radial-gradient(ellipse 60% 40% at 80% 60%, rgba(56,189,248,0.05) 0%, transparent 70%);
                    pointer-events: none;
                }

                /* ── Header ── */
                .hp-header {
                    text-align: center;
                    margin-bottom: 52px;
                }
                .hp-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    gap: 14px;
                    margin-bottom: 20px;
                }
                .hp-eyebrow-line {
                    width: 40px;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #6c5ce7, transparent);
                }
                .hp-eyebrow-text {
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 3px;
                    text-transform: uppercase;
                    color: #818cf8;
                }
                .hp-title {
                    color: #ffffff;
                    font-size: clamp(1.9rem, 3.5vw, 2.8rem);
                    font-weight: 800;
                    line-height: 1.2;
                    margin-bottom: 18px;
                    letter-spacing: -0.5px;
                }
                .hp-subtitle {
                    color: rgba(255,255,255,0.58);
                    font-size: 15px;
                    line-height: 1.75;
                    max-width: 680px;
                    margin: 0 auto;
                }
                .hp-header-actions {
                    display: flex;
                    justify-content: center;
                    margin-top: 24px;
                }
                .hp-header-catalog-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 24px;
                    border-radius: 30px;
                    background: rgba(99, 102, 241, 0.15);
                    border: 1px solid rgba(99, 102, 241, 0.4);
                    color: #c7d2fe;
                    font-size: 13.5px;
                    font-weight: 700;
                    letter-spacing: 0.3px;
                    text-decoration: none;
                    transition: all 0.25s ease;
                    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.15);
                }
                .hp-header-catalog-link:hover {
                    background: #6366f1;
                    color: #ffffff;
                    border-color: #6366f1;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4);
                }

                /* ── Category tabs ── */
                .hp-cat-tabs {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 8px;
                    margin-bottom: 32px;
                }
                .hp-cat-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 7px 16px;
                    border-radius: 6px;
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.09);
                    color: rgba(255,255,255,0.65);
                    font-size: 12px;
                    font-weight: 600;
                    letter-spacing: 0.3px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .hp-cat-btn:hover {
                    background: rgba(255,255,255,0.07);
                    color: #fff;
                    border-color: rgba(255,255,255,0.18);
                }
                .hp-cat-btn.active {
                    background: rgba(108,92,231,0.22);
                    border-color: rgba(108,92,231,0.6);
                    color: #c4b5fd;
                }
                .hp-cat-count {
                    font-size: 10px;
                    padding: 1px 6px;
                    border-radius: 4px;
                    background: rgba(0,0,0,0.3);
                    color: rgba(255,255,255,0.5);
                }

                /* ── Controls bar ── */
                .hp-controls-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    padding: 0 2px;
                }
                .hp-controls-left {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .hp-slide-counter {
                    font-size: 14px;
                    font-weight: 700;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.1);
                    padding: 5px 14px;
                    border-radius: 6px;
                }
                .hp-counter-n { color: #818cf8; }
                .hp-counter-sep { color: rgba(255,255,255,0.25); }
                .hp-counter-t { color: rgba(255,255,255,0.5); }
                .hp-controls-hint {
                    font-size: 12px;
                    color: rgba(255,255,255,0.4);
                    letter-spacing: 0.2px;
                }
                .hp-controls-right {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .hp-ctrl-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 6px;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.1);
                    color: rgba(255,255,255,0.7);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 11px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .hp-ctrl-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
                .hp-arrow-btn {
                    width: 40px;
                    height: 40px;
                    border-radius: 6px;
                    background: rgba(108,92,231,0.12);
                    border: 1px solid rgba(108,92,231,0.35);
                    color: #c4b5fd;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 13px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .hp-arrow-btn:hover {
                    background: #6c5ce7;
                    border-color: #6c5ce7;
                    color: #fff;
                    box-shadow: 0 4px 16px rgba(108,92,231,0.4);
                }

                /* ── Slider track ── */
                .hp-track {
                    display: flex;
                    gap: 24px;
                    overflow-x: auto;
                    scroll-behavior: smooth;
                    scroll-snap-type: x mandatory;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                    padding: 16px 4px 40px;
                    cursor: grab;
                }
                .hp-track::-webkit-scrollbar { display: none; }

                /* ── Slide item widths ── */
                .hp-slide-item {
                    flex: 0 0 calc(33.333% - 16px);
                    scroll-snap-align: start;
                    min-width: 300px;
                    box-sizing: border-box;
                }
                @media (max-width: 991px) {
                    .hp-slide-item { flex: 0 0 calc(50% - 12px); }
                }
                @media (max-width: 600px) {
                    .hp-slide-item { flex: 0 0 calc(100% - 8px); }
                }

                /* ── Card ── */
                .hp-card {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: rgba(10, 14, 35, 0.82);
                    backdrop-filter: blur(16px);
                    border: 1px solid rgba(255,255,255,0.07);
                    border-radius: 16px;
                    overflow: hidden;
                    transition: transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s, border-color 0.3s;
                    cursor: pointer;
                    position: relative;
                }
                .hp-card:hover {
                    transform: translateY(-8px);
                    border-color: rgba(var(--accent-rgb, 108,92,231), 0.35);
                    box-shadow:
                        0 20px 50px rgba(0,0,0,0.55),
                        0 0 0 1px rgba(255,255,255,0.05);
                }
                .hp-card-topbar {
                    height: 3px;
                    width: 100%;
                    flex-shrink: 0;
                    opacity: 0.85;
                }

                /* ── Box stage ── */
                .hp-box-stage {
                    padding: 36px 20px 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 330px;
                    background: #080b1a;
                    position: relative;
                }

                /* ── Divider ── */
                .hp-card-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
                    margin: 0 20px;
                }

                /* ── Card body ── */
                .hp-card-body {
                    padding: 20px 22px 22px;
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                    gap: 10px;
                }
                .hp-card-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                .hp-tag-cat {
                    font-size: 10px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: #818cf8;
                }
                .hp-tag-featured {
                    font-size: 10px;
                    font-weight: 700;
                    padding: 2px 8px;
                    border-radius: 4px;
                    background: rgba(251,191,36,0.15);
                    color: #fbbf24;
                    border: 1px solid rgba(251,191,36,0.3);
                }
                .hp-card-title {
                    font-size: 1.08rem;
                    font-weight: 800;
                    color: #ffffff;
                    line-height: 1.35;
                    margin: 0;
                    letter-spacing: -0.2px;
                }
                .hp-card-tagline {
                    font-size: 12.5px;
                    color: rgba(255,255,255,0.55);
                    line-height: 1.55;
                    margin: 0;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                /* ── Feature list ── */
                .hp-feat-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .hp-feat-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 12px;
                    color: rgba(255,255,255,0.75);
                }
                .hp-feat-dot {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }

                /* ── Price row ── */
                .hp-price-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 10px 12px;
                    background: rgba(255,255,255,0.025);
                    border: 1px solid rgba(255,255,255,0.06);
                    border-radius: 8px;
                    margin-top: auto;
                }
                .hp-price-lbl {
                    display: block;
                    font-size: 9px;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    color: rgba(255,255,255,0.38);
                    margin-bottom: 2px;
                }
                .hp-price-val {
                    font-size: 1rem;
                    font-weight: 800;
                    color: #38bdf8;
                }
                .hp-price-arr {
                    font-size: 0.95rem;
                    font-weight: 800;
                    color: #34d399;
                }
                .hp-price-arr small {
                    font-size: 9px;
                    color: rgba(255,255,255,0.4);
                    margin-left: 2px;
                }

                /* ── CTA button ── */
                .hp-cta-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.1);
                    color: rgba(255,255,255,0.85);
                    font-size: 12px;
                    font-weight: 700;
                    padding: 10px 16px;
                    border-radius: 8px;
                    text-decoration: none;
                    letter-spacing: 0.3px;
                    transition: all 0.22s;
                }
                .hp-cta-btn:hover {
                    background: var(--accent, #6c5ce7);
                    border-color: var(--accent, #6c5ce7);
                    color: #fff;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.3);
                }

                /* ── Pagination dots ── */
                .hp-dots {
                    display: flex;
                    justify-content: center;
                    gap: 6px;
                    margin-top: 8px;
                }
                .hp-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    border: none;
                    background: rgba(255,255,255,0.18);
                    cursor: pointer;
                    transition: all 0.3s;
                    padding: 0;
                }
                .hp-dot.active {
                    width: 24px;
                    border-radius: 3px;
                    background: #6c5ce7;
                    box-shadow: 0 0 8px rgba(108,92,231,0.6);
                }

                /* ── Footer CTAs ── */
                .hp-footer-cta {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 16px;
                    flex-wrap: wrap;
                    margin-top: 52px;
                    padding-top: 40px;
                    border-top: 1px solid rgba(255,255,255,0.06);
                }
                .hp-footer-link {
                    display: inline-flex;
                    align-items: center;
                    background: linear-gradient(135deg, #6c5ce7, #4f46e5);
                    color: #fff;
                    font-weight: 700;
                    font-size: 13px;
                    padding: 12px 28px;
                    border-radius: 8px;
                    text-decoration: none;
                    transition: all 0.25s;
                    box-shadow: 0 8px 24px rgba(108,92,231,0.3);
                    letter-spacing: 0.2px;
                }
                .hp-footer-link:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 30px rgba(108,92,231,0.5);
                    color: #fff;
                }
                .hp-footer-link-sec {
                    display: inline-flex;
                    align-items: center;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.12);
                    color: rgba(255,255,255,0.75);
                    font-weight: 600;
                    font-size: 13px;
                    padding: 12px 24px;
                    border-radius: 8px;
                    text-decoration: none;
                    transition: all 0.25s;
                }
                .hp-footer-link-sec:hover {
                    background: rgba(255,255,255,0.08);
                    color: #fff;
                    border-color: rgba(255,255,255,0.2);
                }
            `}</style>
        </section>
    );
};

export default HomeProducts;
